// src/components/staff/StaffForm.js
import React, { useState, useEffect } from 'react';
import Toast from '../common/Toast';
import SearchableSelect from '../common/SearchableSelect';

const StaffForm = ({ staff, premises, organizations, onSubmit, onCancel, userRole, designations, onPremisesChange, onOrganizationChange, countries, userOrgCountryCode }) => {
    const normalizeDialCode = (dialCode) => (dialCode?.startsWith('+') ? dialCode : `+${dialCode}`);

    // Determine initial dial code based on user role and organization
    const getInitialDialCode = () => {
        if (userRole !== 'core_admin' && userOrgCountryCode && countries && countries.length > 0) {
            // Find country by code (not id) since userOrgCountryCode is the country_code from organization table
            const orgCountry = countries.find(c => c.code === userOrgCountryCode);
            if (orgCountry && orgCountry.dial_code) {
                return normalizeDialCode(orgCountry.dial_code);
            }
        }
        return userRole === 'core_admin' ? '+1' : '+234'; // Core admin defaults to US
    };

    const [formData, setFormData] = useState({
        name: '',
        staff_id: '',
        email: '',
        password: '',
        phone_number: '',
        country_code: getInitialDialCode(),
        country_iso_code: userRole === 'core_admin' ? 'US' : (userOrgCountryCode || ''),
        designation: '',
        organization_id: '',
        premises_id: '',
        date_of_appointment: '',
        gender: '',
        dob: '',
        is_active: 1,
        user_type: 'staff', // Default user type
        transfer_remarks: '' // Transfer remarks for premises change
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [originalPremisesId, setOriginalPremisesId] = useState(null);
    const [premisesChanged, setPremisesChanged] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [countryList, setCountryList] = useState([]);
    const [userDialCode, setUserDialCode] = useState(getInitialDialCode());

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    // Helper function to get image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;

        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }

        const apiUrl = process.env.REACT_APP_API_URL;
        //console.log("API URL ", apiUrl);
        const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;

        return `${apiUrl}${cleanPath}`;
    };

    useEffect(() => {
        // Set country list from props
        if (countries && countries.length > 0) {
            setCountryList(countries);

            // For non-core admins, get their organization's country dial code
            if (userRole !== 'core_admin' && userOrgCountryCode) {
                // Find country by code (not id) since userOrgCountryCode is the country_code from organization table
                const orgCountry = countries.find(c => c.code === userOrgCountryCode);
                if (orgCountry && orgCountry.dial_code) {
                    const dialCode = normalizeDialCode(orgCountry.dial_code);
                    setUserDialCode(dialCode);

                    // Set it as default for new staff (not when editing)
                    if (!staff) {
                        setFormData(prev => ({
                            ...prev,
                            country_code: dialCode,
                            country_iso_code: orgCountry.code || userOrgCountryCode || ''
                        }));
                    }
                }
            }
        }
    }, [countries, userRole, userOrgCountryCode, staff]);

    // App admin: auto-assign their premises on new staff creation
    useEffect(() => {
        if (userRole === 'app_admin' && !staff && premises && premises.length > 0) {
            const id = premises[0].id;
            setFormData(prev => ({ ...prev, premises_id: id }));
            if (onPremisesChange) onPremisesChange(id);
        }
    }, [userRole, staff, premises]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (staff) {
            // Parse phone number to extract country code
            let phoneNumber = (staff.phone_number || '').trim();
            let countryCode = userDialCode; // Use organization's dial code
            let preferredCountryIso = '';

            // Check if phone number starts with +
            if (phoneNumber && phoneNumber.startsWith('+')) {
                const normalizedPhone = phoneNumber.replace(/\s+/g, '');
                const dialCodes = [...new Set(
                    (countryList || [])
                        .map(c => c?.dial_code)
                        .filter(Boolean)
                        .map(normalizeDialCode)
                        .sort((a, b) => b.length - a.length)
                )];

                const matchedDialCode = dialCodes.find(code => normalizedPhone.startsWith(code));

                if (matchedDialCode) {
                    countryCode = matchedDialCode;
                    phoneNumber = normalizedPhone.slice(matchedDialCode.length).trim();
                } else {
                    // Fallback when dial code is missing in country list
                    const match = normalizedPhone.match(/^(\+\d{1,4})(.*)$/);
                    if (match) {
                        countryCode = match[1];
                        phoneNumber = match[2].trim();
                    }
                }
            }

            // Use org country as tie-breaker for shared dial codes (e.g., +1)
            const orgCountryCode = typeof staff.org_country_code === 'string'
                ? staff.org_country_code.toUpperCase()
                : '';
            const matchingCountries = (countryList || []).filter(
                c => normalizeDialCode(c.dial_code) === countryCode
            );

            if (orgCountryCode && matchingCountries.some(c => c.code === orgCountryCode)) {
                preferredCountryIso = orgCountryCode;
            } else if (matchingCountries.length === 1) {
                preferredCountryIso = matchingCountries[0].code;
            } else if (userRole === 'core_admin' && countryCode === '+1') {
                preferredCountryIso = 'US';
            }

            setFormData({
                name: staff.name || '',
                staff_id: staff.staff_id || '',
                email: staff.email || '',
                password: '',
                phone_number: phoneNumber,
                country_code: countryCode,
                country_iso_code: preferredCountryIso || (userRole === 'core_admin' ? 'US' : ''),
                designation: staff.designation || '',
                organization_id: staff.org_id || '',
                premises_id: staff.premises_id || '',
                date_of_appointment: staff.date_of_appointment ? staff.date_of_appointment.split('T')[0] : '',
                gender: staff.gender || '',
                dob: staff.dob ? staff.dob.split('T')[0] : '',
                is_active: staff.is_active !== undefined ? staff.is_active : 1,
                user_type: staff.user_type || 'staff', // Set user_type from existing staff data
                transfer_remarks: '' // Reset transfer remarks when loading staff
            });

            // Store original premises ID for transfer detection
            setOriginalPremisesId(staff.premises_id || '');
            setPremisesChanged(false);

            // Set existing image preview if staff has an image
            if (staff.image) {
                setPhotoPreview(getImageUrl(staff.image));
                setPhotoFile(null); // Reset file since we're showing existing image
            } else {
                setPhotoPreview(null);
                setPhotoFile(null);
            }
        } else {
            // Reset form for new staff - keep default user_type as 'staff'
            setFormData(prev => ({
                ...prev,
                name: '',
                staff_id: '',
                email: '',
                password: '',
                phone_number: '',
                country_code: userDialCode, // Use the organization's dial code
                country_iso_code: userRole === 'core_admin' ? 'US' : (userOrgCountryCode || ''),
                designation: '',
                organization_id: '',
                // app_admin's premises_id is auto-set by the sibling effect — preserve it
                premises_id: userRole === 'app_admin' ? prev.premises_id : '',
                date_of_appointment: '',
                gender: '',
                dob: '',
                is_active: 1,
                user_type: 'staff' // Default for new staff
            }));
            setPhotoPreview(null);
            setPhotoFile(null);
        }
    }, [staff, userDialCode, countryList, userRole, userOrgCountryCode]);

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                showToast('Please select a valid image file', 'error');
                return;
            }

            // Validate file size (e.g., 5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                showToast('Image size should be less than 5MB', 'error');
                return;
            }

            setPhotoFile(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setPhotoPreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleOrganizationChange = (value) => {
        setFormData(prev => ({
            ...prev,
            organization_id: value,
            premises_id: '', // Reset premises when organization changes
            designation: '' // Reset designation when organization changes
        }));

        // Clear organization error
        if (errors.organization_id) {
            setErrors(prev => ({
                ...prev,
                organization_id: ''
            }));
        }

        // Fetch premises for the selected organization
        if (onOrganizationChange && value) {
            onOrganizationChange(value);
        }
    };

    const handleCountryChange = (e) => {
        const isoCode = e.target.value;
        const selectedCountry = (countryList || []).find(country => country.code === isoCode);
        const fallbackDialCodes = { PK: '+92', US: '+1', GB: '+44', IN: '+91' };
        const dialCode = selectedCountry?.dial_code
            ? normalizeDialCode(selectedCountry.dial_code)
            : (fallbackDialCodes[isoCode] || formData.country_code);

        setFormData(prev => ({
            ...prev,
            country_iso_code: isoCode,
            country_code: dialCode
        }));
    };

    const handlePremisesChange = (value) => {
        setFormData(prev => ({
            ...prev,
            premises_id: value,
            designation: '' // Reset designation when premises changes
        }));

        // Detect if premises is being changed during edit (transfer scenario)
        if (staff && originalPremisesId && value !== originalPremisesId) {
            setPremisesChanged(true);
        } else {
            setPremisesChanged(false);
        }

        // Clear premises error
        if (errors.premises_id) {
            setErrors(prev => ({
                ...prev,
                premises_id: ''
            }));
        }

        // Fetch designations for the selected premises
        if (onPremisesChange && value) {
            onPremisesChange(value);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.staff_id.trim()) {
            newErrors.staff_id = 'Staff ID is required';
        }

        if (!formData.premises_id && userRole !== 'app_admin') {
            newErrors.premises_id = 'Premises is required';
        }

        // Organization is required for core admin
        if (userRole === 'core_admin' && !formData.organization_id) {
            newErrors.organization_id = 'Organization is required';
        }

        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (formData.password && formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        // Phone number validation - allow only digits (country code is separate)
        /*if (formData.phone_number) {
            const cleanPhone = formData.phone_number.replace(/[\s\-]/g, '');
            if (!/^[0-9]{7,15}$/.test(cleanPhone)) {
                newErrors.phone_number = 'Phone number must be 7-15 digits (without country code)';
            }
        }*/


        // Add validation for user_type if necessary, e.g., ensure only one app_admin per premise
        // This might require more complex logic involving fetching current app_admins for the premise

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            // Concatenate country code with phone number before submission
            const dataToSubmit = {
                ...formData,
                phone_number: formData.phone_number
                    ? `${formData.country_code}${formData.phone_number.replace(/[\s\-]/g, '')}`
                    : ''
            };

            // Remove country_code from the data as it's now part of phone_number
            delete dataToSubmit.country_code;
            delete dataToSubmit.country_iso_code;

            // Keep existing password if no new value is entered during edit.
            if (!dataToSubmit.password) {
                delete dataToSubmit.password;
            }

            await onSubmit(dataToSubmit, photoFile);
        } catch (error) {
            console.error('Form submission error:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateStaffId = () => {
        const prefix = 'EMP';
        const randomNum = Math.floor(Math.random() * 9999) + 1;
        const paddedNum = randomNum.toString().padStart(4, '0');
        setFormData(prev => ({
            ...prev,
            staff_id: `${prefix}${paddedNum}`
        }));
    };

    // Determine available user types based on current user's role and whether editing
    const availableUserTypes = [];
    if (userRole === 'core_admin') {
        availableUserTypes.push({ value: 'staff', label: 'Staff' });
        availableUserTypes.push({ value: 'app_admin', label: 'App Admin' });
        availableUserTypes.push({ value: 'client_admin', label: 'Client Admin' }); // Only core_admin can assign client_admin
    } else if (userRole === 'client_admin') {
        availableUserTypes.push({ value: 'staff', label: 'Staff' });
        availableUserTypes.push({ value: 'app_admin', label: 'App Admin' }); // Client admin can assign app_admin
        // Note: Assigning 'client_admin' is not allowed for client_admin
    } else {
        // For app_admin or other roles, they might not be able to change user_type
        // Or only see their current type if editing themselves (though this form is likely for others)
        // For now, assume they can't change it if they aren't core/client admin
        // This part might need more specific logic based on your requirements
        // For now, hide the dropdown for non-core/client admins or just show current type disabled
    }

    const canEditUserType = (userRole === 'core_admin' || userRole === 'client_admin') && availableUserTypes.length > 0;
    const priorityCountryCode = formData.country_iso_code || staff?.org_country_code || (userRole === 'core_admin' ? 'US' : '');
    const sortedCountryList = userRole === 'core_admin' && countryList?.length > 0
        ? [...countryList].sort((a, b) => {
            if (a.code === priorityCountryCode) return -1;
            if (b.code === priorityCountryCode) return 1;
            return 0;
        })
        : countryList;
    const designationOptions = Array.isArray(designations) ? designations : [];
    const hasDesignationOptions = designationOptions.length > 0;
    const currentDesignationMissing =
        Boolean(formData.designation) &&
        !designationOptions.some(
            (item) =>
                (item?.designation_name || '').toString().trim().toLowerCase() ===
                formData.designation.toString().trim().toLowerCase()
        );

    return (
        <form onSubmit={handleSubmit} className="staff-form">
            <div className="form-section">
                <div className="form-group">
                    <label>Staff Photo</label>
                    <div className="photo-upload-section">
                        {photoPreview && (
                            <div className="photo-preview">
                                <img src={photoPreview} alt="Staff Preview" className="preview-image" />
                                <button
                                    type="button"
                                    className="remove-photo-btn"
                                    onClick={() => {
                                        setPhotoFile(null);
                                        setPhotoPreview(null);
                                        // Reset file input to allow selecting the same file again
                                        const fileInput = document.getElementById('staff-photo');
                                        if (fileInput) fileInput.value = '';
                                    }}
                                    title="Remove photo"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        )}

                        <div className="photo-upload-controls">
                            <input
                                type="file"
                                id="staff-photo"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                className="photo-input"
                                style={{ display: 'none' }}
                            />
                            <label htmlFor="staff-photo" className="photo-upload-btn">
                                <i className="fas fa-camera"></i>
                                {photoPreview ? (staff && staff.image ? 'Change Photo' : 'Change Photo') : 'Upload Photo'}
                            </label>
                            <small>Supported formats: JPG, PNG, GIF (Max 5MB)</small>
                        </div>
                    </div>
                </div>

                <h4>Personal Information</h4>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="name" className="form-label">
                            Full Name <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`form-input ${errors.name ? 'error' : ''}`}
                            placeholder="Enter full name"
                            required
                        />
                        {errors.name && <span className="form-error">{errors.name}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="staff_id" className="form-label">
                            Staff ID <span className="required">*</span>
                        </label>
                        <div className="input-with-button">
                            <input
                                type="text"
                                id="staff_id"
                                name="staff_id"
                                value={formData.staff_id}
                                onChange={handleChange}
                                className={`form-input ${errors.staff_id ? 'error' : ''}`}
                                placeholder="Enter staff ID"
                                required
                            />
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={generateStaffId}
                                title="Generate Staff ID"
                            >
                                <i className="fas fa-magic"></i>
                            </button>
                        </div>
                        {errors.staff_id && <span className="form-error">{errors.staff_id}</span>}
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="email" className="form-label">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`form-input ${errors.email ? 'error' : ''}`}
                            placeholder="Enter email address"
                        />
                        {errors.email && <span className="form-error">{errors.email}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone_number" className="form-label">Phone Number</label>
                        <div className="phone-input-group">
                            {userRole === 'core_admin' ? (
                                <select
                                    name="country_iso_code"
                                    value={formData.country_iso_code || ''}
                                    onChange={handleCountryChange}
                                    className="country-code-select"
                                    title="Select country code"
                                >
                                    {sortedCountryList && sortedCountryList.length > 0 ? (
                                        sortedCountryList.map((country) => {
                                            const dialCode = normalizeDialCode(country.dial_code);
                                            return (
                                                <option key={country.id} value={country.code}>
                                                    {country.code} ({dialCode})
                                                </option>
                                            );
                                        })
                                    ) : (
                                        <>
                                            <option value="PK">PK (+92)</option>
                                            <option value="US">US (+1)</option>
                                            <option value="GB">GB (+44)</option>
                                            <option value="IN">IN (+91)</option>
                                        </>
                                    )}
                                </select>
                            ) : (
                                <div className="country-code-display">
                                    {formData.country_code}
                                </div>
                            )}
                            <input
                                type="tel"
                                id="phone_number"
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleChange}
                                className={`form-input phone-number-input ${errors.phone_number ? 'error' : ''}`}
                                placeholder="Enter phone number"
                            />
                        </div>
                        <small className="form-help">Full number: {formData.country_code}{formData.phone_number}</small>
                        {errors.phone_number && <span className="form-error">{errors.phone_number}</span>}
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="gender" className="form-label">Gender</label>
                        <select
                            id="gender"
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            className="form-input"
                        >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="dob" className="form-label">Date of Birth</label>
                        <input
                            type="date"
                            id="dob"
                            name="dob"
                            value={formData.dob}
                            onChange={handleChange}
                            className={`form-input ${errors.dob ? 'error' : ''}`}
                        />
                        {errors.dob && <span className="form-error">{errors.dob}</span>}
                    </div>
                </div>
            </div>

            <div className="form-section">
                <h4>Job Information</h4>

                <div className="form-row">
                    {/* Organization Selection - Only for Core Admin */}
                    {userRole === 'core_admin' && (
                        <div className="form-group">
                            <label className="form-label">
                                Organization <span className="required">*</span>
                            </label>
                            <SearchableSelect
                                value={formData.organization_id}
                                onChange={handleOrganizationChange}
                                options={organizations || []}
                                placeholder="Select Organization"
                                displayKey="name"
                                valueKey="id"
                                searchPlaceholder="Search organizations..."
                                className={errors.organization_id ? 'error' : ''}
                                required
                            />
                            {errors.organization_id && <span className="form-error">{errors.organization_id}</span>}
                        </div>
                    )}

                    {userRole === 'app_admin' ? (
                        <div className="form-group">
                            <label className="form-label">Premises</label>
                            <input
                                type="text"
                                className="form-input"
                                value={premises?.[0]?.premises_name || ''}
                                disabled
                                readOnly
                            />
                            <small className="form-help">Staff will be added to your premises.</small>
                        </div>
                    ) : (
                        <div className="form-group">
                            <label className="form-label">
                                {staff ? 'Transfer Staff To' : 'Premises'} <span className="required">*</span>
                            </label>
                            <SearchableSelect
                                value={formData.premises_id}
                                onChange={handlePremisesChange}
                                options={premises || []}
                                placeholder={
                                    userRole === 'core_admin' && !formData.organization_id
                                        ? "Select organization first"
                                        : "Select Premises"
                                }
                                displayKey="premises_name"
                                valueKey="id"
                                searchPlaceholder="Search premises..."
                                disabled={userRole === 'core_admin' && !formData.organization_id}
                                className={errors.premises_id ? 'error' : ''}
                                required
                            />
                            {errors.premises_id && <span className="form-error">{errors.premises_id}</span>}
                            {userRole === 'core_admin' && !formData.organization_id && (
                                <small className="form-help">Please select organization first</small>
                            )}
                            {premises && premises.length === 0 && formData.organization_id && (
                                <small className="form-help">No premises available for selected organization</small>
                            )}
                        </div>
                    )}
                </div>

                {/* Transfer Remarks - Show only when editing and premises changed (not for app_admin) */}
                {staff && premisesChanged && userRole !== 'app_admin' && (
                    <div className="form-row">
                        <div className="form-group" style={{width: '100%'}}>
                            <label htmlFor="transfer_remarks" className="form-label">
                                Transfer Remarks
                            </label>
                            <textarea
                                id="transfer_remarks"
                                name="transfer_remarks"
                                value={formData.transfer_remarks}
                                onChange={handleChange}
                                className="form-input"
                                placeholder="Enter reason for transfer or any additional notes..."
                                rows="3"
                                style={{resize: 'vertical'}}
                            />
                            <small className="form-help">
                                This staff member will be transferred from their current premises to the selected premises.
                                You can add notes about the reason for this transfer.
                            </small>
                        </div>
                    </div>
                )}

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="designation" className="form-label">Designation</label>
                        {hasDesignationOptions ? (
                            <select
                                id="designation"
                                name="designation"
                                value={formData.designation}
                                onChange={handleChange}
                                className="form-input"
                                disabled={!formData.premises_id}
                            >
                                <option value="">Select Designation</option>
                                {currentDesignationMissing && (
                                    <option value={formData.designation}>
                                        {formData.designation} (Current)
                                    </option>
                                )}
                                {designationOptions.map(designation => (
                                    <option key={designation.id} value={designation.designation_name}>
                                        {designation.designation_name}
                                        {designation.premises_id === 0 && ' (Common)'}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                id="designation"
                                name="designation"
                                value={formData.designation}
                                onChange={handleChange}
                                className="form-input"
                                placeholder={formData.premises_id ? 'Enter designation' : 'Select premises first'}
                                disabled={!formData.premises_id}
                            />
                        )}
                        {!formData.premises_id && (
                            <small className="form-help">Please select premises first</small>
                        )}
                        {formData.premises_id && !hasDesignationOptions && (
                            <small className="form-help">No predefined designations found. You can type one.</small>
                        )}
                    </div>

                    {/* NEW: User Type Selection */}
                    {canEditUserType && (
                        <div className="form-group">
                            <label htmlFor="user_type" className="form-label">User Type</label>
                            <select
                                id="user_type"
                                name="user_type"
                                value={formData.user_type}
                                onChange={handleChange}
                                className="form-input"
                            // Optionally disable for app_admins or based on other logic
                            // disabled={userRole !== 'core_admin' && userRole !== 'client_admin'}
                            >
                                {availableUserTypes.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                            <small>Assign the role for this staff member.</small>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="date_of_appointment" className="form-label">Date of Appointment</label>
                        <input
                            type="date"
                            id="date_of_appointment"
                            name="date_of_appointment"
                            value={formData.date_of_appointment}
                            onChange={handleChange}
                            className="form-input"
                        />
                    </div>
                </div>

                <div className="form-row">
                    {staff && (
                        <div className="form-group">
                            <label htmlFor="password" className="form-label">New Password (Optional)</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`form-input ${errors.password ? 'error' : ''}`}
                                placeholder="Enter new password"
                                autoComplete="new-password"
                            />
                            {errors.password && <span className="form-error">{errors.password}</span>}
                            <small className="form-help">Leave blank to keep current password.</small>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="is_active" className="form-label">Status</label>
                        <select
                            id="is_active"
                            name="is_active"
                            value={formData.is_active}
                            onChange={handleChange}
                            className="form-input"
                        >
                            <option value={1}>Active</option>
                            <option value={0}>Disabled</option>
                        </select>
                    </div>

                    <div className="form-group">
                        {/* Empty space for alignment */}
                    </div>
                </div>
            </div>

            <div className="form-actions">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <div className="spinner"></div>
                            {staff ? 'Updating...' : 'Creating...'}
                        </>
                    ) : (
                        <>
                            <i className="fas fa-save"></i>
                            {staff ? 'Update Staff' : 'Create Staff'}
                        </>
                    )}
                </button>
            </div>



            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />
        </form>
    );
};

export default StaffForm;
